/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import { REQUEST_STATUS_COMPLETED, REQUEST_STATUS_FAILED } from '../../../../share/util/api/polling';
import session from '../../../../share/session';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import { apiErrorDefaultAction } from '../../../../share/util/api/errorAction';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';

/** 各フェーズ定義 */
const PHASE = {
  STANDBY: 'standby',
  START: 'start',
  CAUTION: 'caution',
  CONFIRMATION: 'confirmation',
  EXECUTION: 'execution',
  SUCCESS: 'success',
  FAILED: 'failed'
};

export default {
  name: 'requirement104',
  data() {
    return {
      phase: PHASE.STANDBY,
      isLoading: false,
      systemId: '',
      displayInfo: '',
      modal_requirement_104: false
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),
    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.FAILED:
          return 'danger';
        case PHASE.CAUTION:
          return 'warning';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.STANDBY:
        case PHASE.START:
        case PHASE.CONFIRMATION:
        case PHASE.EXECUTION:
        default:
          return 'secondary';
      }
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_requirement_104 = true;
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
    },

    /** OK、NO、キャンセルボタン押下(終了処理) */
    async exitProcess() {
      await this.exit();
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result handler.validate で返ってきたバリデーション結果
     */
    validateErrorResult(result) {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** 初期処理 */
    async standby() {
      try {
        this.isLoading = true;
        const token = session.getToken();

        // 初期処理 (要求)（バックエンドAPI_POST）
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'standby'
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); }
        );

        if (response) {
          // ポーリングが正常に完了した場合
          switch (response.data.status) {
            case REQUEST_STATUS_COMPLETED:
              // 正常終了：開始確認画面表示
              this.displayInfo = this.$t(this.i18nWsKey + '.REQ_WS_104_TXT_1');
              this.phase = PHASE.START;
              break;
            default:
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 開始確認、YES押下 */
    async clickYesStart() {
      // 警告表示
      this.displayInfo = this.$t(this.i18nWsKey + '.REQ_WS_104_TXT_2');
      this.phase = PHASE.CAUTION;
    },

    /** 警告表示、OK押下 */
    async clickOKCaution() {
      // 実行表示
      this.displayInfo = this.$t(this.i18nWsKey + '.REQ_WS_104_TXT_3');
      this.phase = PHASE.CONFIRMATION;
    },

    /** 実行、OK押下 */
    async execute() {
      try {
        this.isLoading = true;
        const token = session.getToken();

        // 実行処理 (要求)（バックエンドAPI_POST）
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'execute'
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); }
        );

        if (response) {
          // ポーリングが正常に完了した場合
          switch (response.data.status) {
            case REQUEST_STATUS_COMPLETED:
              switch (response.data.user_data.value){
                case "FF":
                  // 正常終了：停止（成功）
                  this.displayInfo = this.$t(this.i18nWsKey + '.REQ_WS_104_TXT_5');
                  this.phase = PHASE.SUCCESS;
                  return;
                case "00":
                  // 正常終了：実行監視
                  this.displayInfo = this.$t(this.i18nWsKey + '.REQ_WS_104_TXT_4');
                  this.phase = PHASE.EXECUTION;
                  await this.read(); // データ読み出し処理
                  return;
                default:
                  this.showUnexpectedError();
                  return;
              }
            case REQUEST_STATUS_FAILED:
            default:
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 実行監視、データ読み出し処理 */
    async read() {
      try {
        this.isLoading = true;
        const token = session.getToken();

        // 実行処理 (要求)（バックエンドAPI_POST）
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'read'
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); }
        );

        if (response) {
          // ポーリングが正常に完了した場合
          switch (response.data.status) {
            case REQUEST_STATUS_COMPLETED:
              switch (response.data.user_data.value){
                case "FF":
                  // 正常終了：停止（成功）
                  this.displayInfo = this.$t(this.i18nWsKey + '.REQ_WS_104_TXT_5');
                  this.phase = PHASE.SUCCESS;
                  return;
                case "00":
                  // 正常終了：実行監視（継続）
                  await this.read(); // データ読み出し処理
                  return;
                default:
                  this.showUnexpectedError();
                  return;
              }
            case REQUEST_STATUS_FAILED:
            default:
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 終了処理 */
    async exit() {
      try {
        this.isLoading = true;
        const token = session.getToken();

        // DefaultSession遷移 (要求)（バックエンドAPI_POST）
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'exit'
          },
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列、もしくは独自定義した文字列が返ってくる。
          // 必要に応じて、errorType に応じてメッセージを変えるといったことも可能。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case REQUEST_STATUS_COMPLETED:
              // Wizardを閉じる
              this.closeWizard();
              break;
            case REQUEST_STATUS_FAILED:
            default:
              this.showUnexpectedError();
              break;
          }
        }

      } finally {
        this.isLoading = false;
      }
    },

    /** その他エラーの表示 */
    showUnexpectedError() {
      if (this.phase === PHASE.FAILED) {
        // Wizardを閉じる
        this.closeWizard();
        return;
      }
      // 異常終了：エラー表示
      this.phase = PHASE.FAILED;
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_104 = false;
    },
  },

  async mounted() {
    this.init();
    // 初期処理
    await this.standby();
  },
};